import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { Plus } from 'lucide-react';
import {
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import useAuth from '../auth/auth';
import { Grade } from '../services/api/domains/grades/types';
import {
  useGradesPage,
  useMergeSuggestions,
  useMergePreview,
  useMergeGrades,
} from '../services/api/domains/grades/hooks';
import GradeModal from '../components/modals/GradeModal';
import GenericMergeModal from '../components/modals/GenericMergeModal';
import { ModalMode } from '../components/modals/hooks/useEditableModal';
import TableSearch from 'components/TableSearch';
import {
  SearchMode,
  AdvancedSearchQuery,
  createEmptyAdvancedQuery,
} from 'components/TableSearch/types';
import {
  mergeAdvancedQueries,
  gridFilterToAdvancedQuery,
} from '../utils/dataGridHelpers';
import { gradesFieldConfig } from '../config/searchConfigs';
import LgmDataGrid from 'components/LgmDataGrid';
import {
  toDataGridColumns,
  getDefaultColumnVisibility,
} from '../domains/grades/adapters';

const PAGE_SIZE = 50;

const Grades: React.FC = () => {
  const { isAdmin, userId, isViewer } = useAuth();

  // Search state
  const [searchMode, setSearchMode] = useState<SearchMode>('basic');
  const [basicSearchTerm, setBasicSearchTerm] = useState('');
  const [advancedSearchQuery, setAdvancedSearchQuery] =
    useState<AdvancedSearchQuery>(createEmptyAdvancedQuery());
  const pageRef = useRef(0);

  // DataGrid state
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([]);
  const [gridPaginationModel, setGridPaginationModel] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: PAGE_SIZE,
    });
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(getDefaultColumnVisibility());

  const gradesParams = useMemo(() => {
    // Convert DataGrid filters to AdvancedSearchQuery
    const gridAdvancedQuery = gridFilterToAdvancedQuery(
      gridFilterModel,
      gradesFieldConfig
    );

    // Merge with existing search
    let finalAdvancedSearch: AdvancedSearchQuery | undefined;
    if (searchMode === 'advanced') {
      finalAdvancedSearch = mergeAdvancedQueries(
        advancedSearchQuery,
        gridAdvancedQuery
      );
    } else if (gridAdvancedQuery.filterGroups.length > 0) {
      finalAdvancedSearch = gridAdvancedQuery;
    }

    return {
      search:
        searchMode === 'basic'
          ? basicSearchTerm.trim() || undefined
          : undefined,
      advancedSearch: finalAdvancedSearch,
      sortBy: gridSortModel[0]?.field as keyof Grade | undefined,
      sortDirection: gridSortModel[0]?.sort?.toUpperCase() as
        | 'ASC'
        | 'DESC'
        | undefined,
      limit: gridPaginationModel.pageSize,
      offset: gridPaginationModel.page * gridPaginationModel.pageSize,
    };
  }, [
    searchMode,
    basicSearchTerm,
    advancedSearchQuery,
    gridFilterModel,
    gridSortModel,
    gridPaginationModel,
  ]);

  const {
    create: createGrade,
    update: updateGrade,
    delete: deleteGrade,
    data: grades,
    total,
    refetch: refetchGrades,
  } = useGradesPage(gradesParams);

  // Preserve total during loading to prevent DataGrid from resetting pagination
  const [stableTotal, setStableTotal] = useState(total || 0);
  useEffect(() => {
    if (total !== undefined && total > 0) {
      setStableTotal(total);
    }
  }, [total]);

  // Get DataGrid columns from adapter
  const gradesColumns = useMemo(() => toDataGridColumns(), []);

  const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null);
  const [openGradeModal, setOpenGradeModal] = useState<ModalMode | null>(null);
  const [openMergeModal, setOpenMergeModal] = useState(false);
  const [mergeSourceGrade, setMergeSourceGrade] = useState<Grade | null>(null);

  // Reset page when search changes
  useEffect(() => {
    if (pageRef.current !== 0) {
      console.log('Resetting page to 0 due to search change');
      setGridPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  }, [basicSearchTerm, advancedSearchQuery]);

  useEffect(() => {
    pageRef.current = gridPaginationModel.page;
  }, [gridPaginationModel.page]);

  const handleSaveGrade = useCallback(
    async (gradeData: Grade) => {
      try {
        if (gradeData.grade_id) {
          await updateGrade.mutate({
            grade_id: gradeData.grade_id,
            grade_name: gradeData.grade_name,
            user_id: userId,
          });
        } else {
          await createGrade.mutate({
            grade_name: gradeData.grade_name,
            user_id: userId,
          });
        }
        setSelectedGrade(null);
        setOpenGradeModal(null);
        refetchGrades();
        alert('✅ Product Type saved successfully');
      } catch (err) {
        console.error('❌ Save failed:', err);
        alert('❌ Failed to save product type. Please try again.');
      }
    },
    [updateGrade, createGrade, refetchGrades, userId]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      if (!window.confirm('Delete this product type?')) return;
      try {
        await deleteGrade.mutate(id);
        refetchGrades();
        alert('✅ Product Type deleted successfully');
      } catch (err) {
        console.error('❌ Delete failed:', err);
        alert('❌ Failed to delete product type. Please try again.');
      }
    },
    [deleteGrade, refetchGrades]
  );

  // Memoized handlers for modal actions
  const handleAddGrade = useCallback(() => {
    const newGrade: Grade = {
      grade_id: 0,
      grade_name: '',
      created_date: new Date(),
      modified_date: new Date(),
      user_id: userId,
    };
    setSelectedGrade(newGrade);
    setOpenGradeModal('EDIT');
  }, [userId]);

  const handleCloseGradeModal = useCallback(() => {
    setSelectedGrade(null);
    setOpenGradeModal(null);
  }, []);

  const handleOpenMergeModal = useCallback((grade: Grade) => {
    setMergeSourceGrade(grade);
    setOpenMergeModal(true);
  }, []);

  const handleCloseMergeModal = useCallback(() => {
    setMergeSourceGrade(null);
    setOpenMergeModal(false);
  }, []);

  const handleMergeSuccess = useCallback(() => {
    refetchGrades();
    alert('✅ Product Types merged successfully');
  }, [refetchGrades]);

  return (
    <div style={styles.pageContainerStyle}>
      <div style={styles.searchBarContainerStyle}>
        <div style={styles.searchUploadContainerStyle}>
          <TableSearch
            mode={searchMode}
            onModeChange={setSearchMode}
            basicSearchTerm={basicSearchTerm}
            onBasicSearchChange={setBasicSearchTerm}
            onBasicSearchClear={() => setBasicSearchTerm('')}
            basicPlaceholder="Search product types..."
            advancedFieldConfigs={gradesFieldConfig}
            advancedQuery={advancedSearchQuery}
            onAdvancedSearch={query => {
              setAdvancedSearchQuery(query);
              setGridPaginationModel(prev => ({ ...prev, page: 0 }));
            }}
            onAdvancedClear={() => {
              setAdvancedSearchQuery(createEmptyAdvancedQuery());
              setGridPaginationModel(prev => ({ ...prev, page: 0 }));
            }}
          />
          {isAdmin && (
            <button onClick={handleAddGrade} style={styles.addButtonStyle}>
              <Plus size={16} /> Add Product Type
            </button>
          )}
        </div>
      </div>

      <div style={styles.tableContainerStyle}>
        <LgmDataGrid<Grade>
          data={grades}
          columns={gradesColumns}
          rowCount={stableTotal}
          paginationModel={gridPaginationModel}
          onPaginationModelChange={setGridPaginationModel}
          sortModel={gridSortModel}
          onSortModelChange={setGridSortModel}
          filterModel={gridFilterModel}
          onFilterModelChange={setGridFilterModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          getRowId={row => row.grade_id}
          onInfo={(g: Grade) => {
            setSelectedGrade(g);
            setOpenGradeModal('VIEW');
          }}
          onEdit={
            !isViewer
              ? (g: Grade) => {
                  setSelectedGrade(g);
                  setOpenGradeModal('EDIT');
                }
              : undefined
          }
          onDelete={
            !isViewer
              ? (g: Grade) => {
                  handleDelete(g.grade_id);
                }
              : undefined
          }
          onMerge={isAdmin ? (g: Grade) => handleOpenMergeModal(g) : undefined}
        />
      </div>

      {/* Grade Modal */}
      {selectedGrade && (
        <GradeModal
          grade={selectedGrade}
          initialMode={openGradeModal || 'VIEW'}
          save={handleSaveGrade}
          close={handleCloseGradeModal}
        />
      )}

      {/* GenericMergeModal for Grades */}
      {openMergeModal && mergeSourceGrade && (
        <GenericMergeModal<Grade>
          source={mergeSourceGrade}
          allEntities={grades}
          config={{
            domainDisplayName: 'Product Type',
            domainDisplayNamePlural: 'Product Types',
            entityIdField: 'grade_id',
            entityNameField: 'grade_name',
            entityCreatedDateField: 'created_date',
            useMergeSuggestions,
            useMergePreview,
            useMerge: useMergeGrades,
          }}
          onClose={handleCloseMergeModal}
          onSuccess={handleMergeSuccess}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  searchUploadContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  titleStyle: {
    fontSize: '1.25rem', // text-xl
    fontWeight: 'bold',
  } as React.CSSProperties,
  addButtonStyle: {
    backgroundColor: '#2563eb', // bg-blue-600
    color: 'white',
    padding: '0.5rem 1rem', // px-4 py-2
    borderRadius: '0.25rem', // rounded
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem', // gap-2
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  pageContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  } as React.CSSProperties,
  searchBarContainerStyle: {
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #d1d5db',
    paddingBottom: '8px',
  } as React.CSSProperties,
  tableContainerStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  } as React.CSSProperties,
};

export default Grades;
